import { faImage } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import AlbumMenu from "@/mk2/apps/blogs/components/AlbumMenu";
import BlogsAlbumDetail from "@/mk2/apps/blogs/containers/BlogsAlbumDetail/BlogsAlbumDetail";
import reducer from "@/mk2/apps/blogs/containers/BlogsAlbumDetail/BlogsAlbumDetail.reducers";
import saga from "@/mk2/apps/blogs/containers/BlogsAlbumDetail/BlogsAlbumDetail.sagas";
import {
    blogsDeletePostTrigger,
    blogsIgnoreUserTrigger,
    blogsSetDisplayingAtFeedTrigger,
} from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.actions";
import blogsSharedSaga from "@/mk2/apps/blogs/containers/BlogsShared/BlogsShared.sagas";
import commentsSaga from "@/mk2/apps/blogs/containers/Comments/Comments.sagas";
import { BLOG_ALBUM_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import { albumBlogsOG } from "@/mk2/apps/blogs/structuredData";
import { blogsAlbumEditPhotosUrl, blogsUrl } from "@/mk2/apps/blogs/urls";
import { getImageIdsFromPosts } from "@/mk2/apps/groups/containers/GroupReportedFeed/GroupReportedFeed.selectors";
import {
    getCommentComplaintsCounts,
    getImageCommentComplaintsCounts,
} from "@/mk2/apps/groups/containers/GroupShared/GroupShared.selectors";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { AppName } from "@/mk2/containers/Application/Application";
import { complaintOpenTrigger } from "@/mk2/containers/Complaint/Complaint.actions";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { menuToggle } from "@/mk2/containers/Menu/Menu.actions";
import { getFirstParagraphsPlaintext } from "@/mk2/helpers/article_ast/utils";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, {
    AnchorLeaderboardProps,
    BrandingProps,
    InterstitialProps,
    PageLayout,
    PageRouteProps,
} from "@/mk2/pages/Page";
import {
    AppState,
    getRequestBaseUrl,
    getRequestDeviceMobile,
    getRequestPermissions,
    getRequestUser,
} from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { BlogPostEntity, BlogPostSchema, UserEntity } from "@/mk2/schemas";
import { getDenormalizedEntity } from "@/mk2/selectors";
import { Store } from "@/mk2/store/configureStore";
import { ADD_PICTURE } from "@/mk/autogenerated/translation";
import { canManageBlogs, hasAds, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";

import { blogsAlbumDetailFetchTrigger } from "./BlogsAlbumDetail.actions";
import { getBlogsAlbumDetailState } from "./BlogsAlbumDetail.selectors";

interface OwnProps {}

interface StateProps {
    loadingState: LoadingState;
    errorMessage: string;
    post: BlogPostEntity;
    commentsCount: number;
    complaintsCount: number;
    imageComplaintsCounts: { [postId: number]: number };
    requestUser: UserEntity;
    requestUserPermissions: string[];
    baseUrl: string;
    isMobile: boolean;
}

interface DispatchProps {
    onDelete(postId: number);
    onLoad(username: string, postSlug: string, accessKey: string);
    onIgnoreUser(username: string);
    onReport(entityType: ComplaintTargetType, postId: number);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onToggleMenuMore(id: number);
}

type RouteProps = PageRouteProps<{
    username: string;
    postSlug: string;
}>;
export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class BlogsAlbumDetailPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const {
                match: {
                    params: { username, postSlug },
                },
            } = ownProps;

            const pageState = getBlogsAlbumDetailState(state, username, postSlug);
            const post: BlogPostEntity = getDenormalizedEntity(
                state,
                BlogPostSchema,
                pageState.postId,
            );

            const requestUserPermissions = getRequestPermissions(state);
            const isBlogsAdmin = canManageBlogs(requestUserPermissions);

            const complaintsCount =
                isBlogsAdmin && post
                    ? getCommentComplaintsCounts(state, [post.id], pageState.loadingState)[post.id]
                    : 0;

            const imageComplaintsCounts =
                isBlogsAdmin && post
                    ? getImageCommentComplaintsCounts(
                          state,
                          getImageIdsFromPosts([post]),
                          pageState.loadingState,
                      )
                    : {};

            return {
                loadingState: pageState.loadingState,
                errorMessage: pageState.errorMessage,
                post,
                commentsCount: pageState.commentIds.length,
                complaintsCount,
                imageComplaintsCounts,
                requestUser: getRequestUser(state),
                requestUserPermissions: getRequestPermissions(state),
                baseUrl: getRequestBaseUrl(state),
                isMobile: getRequestDeviceMobile(state),
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onDelete: blogsDeletePostTrigger,
            onLoad: blogsAlbumDetailFetchTrigger,
            onIgnoreUser: blogsIgnoreUserTrigger,
            onReport: complaintOpenTrigger,
            onSetDisplayingAtFeed: blogsSetDisplayingAtFeedTrigger,
            onToggleMenuMore: (id: number) => menuToggle(BLOG_ALBUM_MENU_MORE(id)),
        };
    }

    public getHeaderTitle(props: Props): string {
        return null;
    }

    public getMetaTitle(props: Props): string {
        const {
            post,
            match: {
                params: { username },
            },
        } = props;
        if (post?.perexAST) {
            const perexText = getFirstParagraphsPlaintext(post.perexAST);
            if (perexText) {
                const prefix = `@${username}: `;
                const maxPerexLength = 120 - prefix.length;
                const truncatedPerex =
                    perexText.length > maxPerexLength
                        ? perexText.substring(0, maxPerexLength - 3) + "..."
                        : perexText;
                return prefix + truncatedPerex;
            }
        }
        return null;
    }

    public getOpenGraphData(
        props: Props,
        baseUrl: string,
        currentUrl: string,
    ): { [key: string]: string } {
        const { post } = props;
        if (post) {
            return {
                ...super.getOpenGraphData(props, baseUrl, currentUrl),
                ...albumBlogsOG(baseUrl, post),
            };
        }
        return null;
    }

    public render(props: Props): false | JSX.Element {
        return <BlogsAlbumDetail {...props} />;
    }

    public getPageLayout(app: AppName, props: Props): PageLayout {
        return PageLayout.MainContent;
    }

    public getBrandingProps(props: Props): BrandingProps {
        const { post, commentsCount } = props;

        if ((post?.images?.length || 0) + (commentsCount || 0) < 5) {
            return null;
        }

        return {
            zone: props.isMobile ? "Blogs Post - Top 01 - Mobile" : "Blogs - Branding",
        };
    }

    public getInterstitialProps(props: Props): InterstitialProps | null {
        return { zone: "Blogs - Interstitial" };
    }

    public getAnchorLeaderboardProps(props: Props): AnchorLeaderboardProps {
        const { commentsCount, isMobile, post, requestUser } = props;

        if (!isMobile || (requestUser && !hasAds(requestUser))) {
            return null;
        }

        if ((post?.images?.length || 0) < 10 && (commentsCount || 0) < 6) {
            return null;
        }

        return {
            zone: "Blogs Feed - Anchor - Mobile",
        };
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return blogsUrl;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        const {
            location,
            post,
            requestUser,
            requestUserPermissions,
            onToggleMenuMore,
            onIgnoreUser,
            onSetDisplayingAtFeed,
            onDelete,
            onReport,
        } = props;

        if (!post) {
            return null;
        }

        const isAuthor = isAuthenticatedAs(requestUser, post.author);
        const isBlogsAdmin = canManageBlogs(requestUserPermissions);

        const albumMenu = (
            <AlbumMenu
                key={"album-menu"}
                isAdmin={isBlogsAdmin}
                isAuthor={isAuthor}
                post={post}
                requestUser={requestUser}
                location={location}
                onDelete={onDelete}
                onIgnoreUser={onIgnoreUser}
                onReport={onReport}
                onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                onToggleMenuMore={onToggleMenuMore}
            />
        );
        const albumAddPictureBtn =
            isAuthor || isBlogsAdmin ? (
                <Btn
                    key={"album-add-image"}
                    type={isAuthor ? BtnType.Blue : isBlogsAdmin ? BtnType.Admin : BtnType.Blue}
                    icon={faImage}
                    label={ADD_PICTURE}
                    link={url(blogsAlbumEditPhotosUrl, {
                        username: post.author.username,
                        postId: post.id,
                    })}
                />
            ) : null;
        return (
            (!!albumMenu || !!albumAddPictureBtn) && (
                <>
                    {albumMenu}
                    {!!albumMenu && !!albumAddPictureBtn && "\u00A0\u00A0"}
                    {albumAddPictureBtn}
                </>
            )
        );
    }

    public getSubmitButton(props: Props): React.ReactElement {
        return null;
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.blogs.blogsAlbumDetails", reducer);
        store.injectSaga("containers.blogs.blogsAlbumDetails", saga);

        store.injectSaga("containers.blogs.blogsShared", blogsSharedSaga);

        store.injectSaga("containers.blogs.shared.comments", commentsSaga);
    }
}
